import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { getCategoriesForStage, getEventDetail } from "../lib/api";
import type { RaceDayCategory, RaceDayEventDetail } from "../lib/types";
import { useWatchList } from "../lib/useWatchList";
import { addEvent, removeEvent } from "../lib/watchlist";
import CopyLinkButton from "../components/CopyLinkButton";

export default function Event() {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const stageId = searchParams.get("s");
  const [event, setEvent] = useState<RaceDayEventDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const watch = useWatchList();

  useEffect(() => {
    let cancelled = false;
    if (!eventId) return;
    setEvent(null);
    setError(null);
    getEventDetail(eventId)
      .then((res) => {
        if (!cancelled) {
          setEvent(res.event);
          // If no stage selected and there's only one (or a default), set it.
          if (!stageId) {
            const sid = res.event.defaultStageId ?? res.event.stages[0]?.id;
            if (sid) {
              const next = new URLSearchParams(searchParams);
              next.set("s", sid);
              setSearchParams(next, { replace: true });
            }
          }
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // RaceDay's category IDs are stage-specific. The event detail HTML only
  // exposes the default stage's categories; for any other stage we have to
  // fetch them via /get_cats_for_stage.
  const [stageCategories, setStageCategories] = useState<RaceDayCategory[] | null>(null);
  const [stageCategoriesLoading, setStageCategoriesLoading] = useState(false);
  useEffect(() => {
    if (!event) return;
    setStageCategories(null);
    if (!stageId || stageId === event.defaultStageId) return;
    let cancelled = false;
    setStageCategoriesLoading(true);
    getCategoriesForStage({ eventId: event.id, stageId })
      .then((res) => {
        if (!cancelled) setStageCategories(res.categories);
      })
      .catch(() => {
        /* fall back to event.categories silently */
      })
      .finally(() => {
        if (!cancelled) setStageCategoriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [event, stageId]);

  if (error) return <div className="text-sm text-pit-accent font-mono">{error}</div>;
  if (!event) return <div className="text-sm text-pit-muted font-mono">Loading…</div>;

  const watched = watch.events.includes(event.id);
  const activeStageId = stageId ?? event.defaultStageId ?? event.stages[0]?.id ?? "";
  const activeStage = event.stages.find((s) => s.id === activeStageId) ?? event.stages[0];
  const showingDefault = !stageId || stageId === event.defaultStageId;
  const categories = showingDefault ? event.categories : stageCategories ?? [];

  const setStage = (sid: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("s", sid);
    setSearchParams(next);
  };

  return (
    <div className="space-y-5">
      <div>
        <Link
          to="/events"
          className="text-xs font-mono text-pit-muted hover:text-pit-text"
        >
          ← all events
        </Link>
        <div className="flex items-start justify-between gap-3 mt-1">
          <div>
            <h1 className="text-xl font-mono font-bold">{event.name}</h1>
            <div className="text-sm text-pit-muted font-mono mt-0.5">
              {event.date}
              {event.info.eventType && ` · ${event.info.eventType}`}
              {event.info.stageType && ` · ${event.info.stageType}`}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 justify-end">
            <button
              className={`btn ${watched ? "border-pit-gold text-pit-gold" : ""}`}
              onClick={() =>
                watched ? removeEvent(event.id) : addEvent(event.id)
              }
            >
              {watched ? "★ watched" : "☆ watch"}
            </button>
            <CopyLinkButton />
          </div>
        </div>
      </div>

      {event.stages.length > 1 && activeStage && (
        <div className="space-y-1.5">
          <div className="text-[11px] uppercase tracking-wider text-pit-muted font-mono">
            Stage
          </div>
          <div className="flex flex-wrap gap-1.5">
            {event.stages.map((stage) => (
              <button
                key={stage.id}
                onClick={() => setStage(stage.id)}
                className={`btn ${stage.id === activeStage.id ? "btn-primary" : ""}`}
              >
                {stage.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        <div className="text-[11px] uppercase tracking-wider text-pit-muted font-mono">
          Categories
        </div>
        {stageCategoriesLoading && categories.length === 0 ? (
          <div className="text-sm text-pit-muted font-mono">Loading categories…</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/event/${event.id}/c/${cat.id}${activeStage ? `?s=${activeStage.id}` : ""}`}
                className="rounded-md border border-pit-border pit-panel px-3 py-2.5 hover:border-pit-accent transition-colors font-mono text-sm flex items-center justify-between"
              >
                <span>{cat.name}</span>
                <span className="text-pit-muted text-xs">→</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
